import {
	AssetTier,
	BigNum,
	MarketStatus,
	PRICE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, MarketId } from '@velocity-exchange/common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ZERO_PRICE_BIGNUM } from 'src/constants/constants';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useWithdrawCollateral } from 'src/hooks/velocityActions/spot-management/useWithdrawCollateral';
import { useAccountLiquidationFlags } from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { useApyBreakdownTooltipData } from 'src/hooks/useApyBreakdownTooltipData';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useDisplayPrecisionForCollateral from 'src/hooks/useDisplayPrecisionForCollateral';
import { CollateralInputV2 } from '../../Inputs/CollateralInput';
import { InputQuickActions } from '../../Inputs/InputQuickActions';
import Button from '../../primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import MarketIcon from '../../Utils/MarketIcon';
import useUnsettledPnl from 'src/components/Modals/CollateralModal/hooks/useUnsettledPnl';
import { createSizePresetClickHandler } from 'src/components/Modals/CollateralModal/utils';
import useModal from 'src/components/Modals/hooks/useModal';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { useCollateralActionGate } from 'src/components/Modals/CollateralModal/hooks/useCollateralActionGate';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import SubAccountSelector from './components/SubAccountSelector';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const BorrowUnsettledAmountMessage = () => {
	const { hasUnsettled, unsettledNumber, handleSettleNow } = useUnsettledPnl();

	if (!hasUnsettled) return null;

	return (
		<div className="flex w-full items-start justify-start gap-1">
			<span>
				You have an unsettled amount of{' '}
				{formatText(unsettledNumber, PRESETS.balance)}.
			</span>
			<TextButton onClick={handleSettleNow} variant="tertiary" size="sm">
				Settle now
			</TextButton>
		</div>
	);
};

const BorrowLimitsDisplay = ({
	borrowLimitBigNum,
	onAmountSelect,
	onMaxSelect,
}: {
	borrowLimitBigNum: BigNum;
	onAmountSelect: (amount: string) => void;
	onMaxSelect: () => void;
}) => {
	return (
		<div className="flex w-full items-start justify-between">
			<span>Borrow Limit</span>
			<InputQuickActions.SizePresets
				onClick={createSizePresetClickHandler({
					maxAmount: borrowLimitBigNum,
					onMaxSelect,
					onAmountSelect,
					format: 'printShort',
				})}
				maxAmount={borrowLimitBigNum}
				testIdPrefix="borrow-modal"
			/>
		</div>
	);
};

const BorrowAPYDisplay = ({
	selectedCollatBank,
	borrowAPYBigNum,
}: {
	selectedCollatBank: SpotMarketConfig;
	borrowAPYBigNum: BigNum;
}) => {
	return (
		<div className="flex w-full items-center justify-between">
			<span>Borrow APY</span>
			<div className="flex items-center gap-1">
				<MarketIcon
					marketSymbol={getCleanedMarketSymbol(selectedCollatBank.symbol)}
					className="size-4"
				/>
				<span className="text-text-default">
					{getCleanedMarketSymbol(selectedCollatBank.symbol)}
				</span>
				<span className="text-star-yellow">{borrowAPYBigNum.toFixed(2)}%</span>
			</div>
		</div>
	);
};

const BalanceChanges = ({
	totalBorrowedAmount,
	afterLiqPriceBigNum,
	selectedCollatBank,
}: {
	totalBorrowedAmount: BigNum;
	afterLiqPriceBigNum: BigNum;
	selectedCollatBank: SpotMarketConfig;
}) => {
	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center justify-between gap-1">
				<span>Total Borrowed</span>
				<span className="text-text-default">
					{`${totalBorrowedAmount.prettyPrint()} ${getCleanedMarketSymbol(
						selectedCollatBank.symbol
					)}`}
				</span>
			</div>
			<div className="flex items-center justify-between gap-1">
				<span>Liquidation Price</span>
				<span className="text-text-default">
					{afterLiqPriceBigNum.lte(BigNum.zero(PRICE_PRECISION_EXP))
						? PRICE_FALLBACK_DISPLAY_VALUE
						: `${formatText(afterLiqPriceBigNum.toNum(), PRESETS.usd)}`}
				</span>
			</div>
		</div>
	);
};

const BorrowModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const { withdrawCollateral } = useWithdrawCollateral();
	const borrowData = useVelocityStore((s) => s.borrowLendData);
	const availableSubaccounts = useLazySubAccounts();
	const { captureEvent } = usePostHogCapture();
	const isMobile = useIsMobileScreenSize();
	const velocityClient = useVelocityClient();
	const { hide } = useModal();

	const {
		updateCollateralIndex,
		handleTargetAccountChange,
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedTargetAccount,
		selectedPoolId,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const [submitting, setSubmitting] = useState(false);
	const [collatTransferAmount, setCollatTransferAmount] = useState('');
	const [isMax, setIsMax] = useState(false);

	const apyData = useApyBreakdownTooltipData(
		selectedCollateralMarket.marketIndex
	);
	const borrowApyBigNum = apyData.borrowing.totalApyBN;

	// Calculate liquidation prices
	const currentLiqPriceBigNum = useMemo(() => {
		return selectedTargetAccount?.client
			? BigNum.from(
					selectedTargetAccount.client.spotLiquidationPrice(
						selectedCollateralMarket.marketIndex
					),
					PRICE_PRECISION_EXP
				)
			: ZERO_PRICE_BIGNUM;
	}, [selectedTargetAccount, selectedCollateralMarket.marketIndex]);

	const [afterLiqPriceBigNum, setAfterLiqPriceBigNum] = useState(
		currentLiqPriceBigNum
	);

	// Get borrow limits and balances - use false for reduceOnly to enable borrowing
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		selectedTargetAccountKey,
		false
	);

	const _collateralDisplayPrecision = useDisplayPrecisionForCollateral(
		selectedCollateralMarket.marketIndex
	);

	const marketId = MarketId.createSpotMarket(
		selectedCollateralMarket.marketIndex
	);

	const spotMarketAccount = velocityClient?.getSpotMarketAccount(
		marketId.marketIndex
	);

	const _markPrice = useMarkPrice(marketId);

	const collatTransferAmountBignum = parseInputBigNumOrZero(
		collatTransferAmount ? collatTransferAmount : '0',
		selectedCollateralMarket.precisionExp
	);

	const borrowLimitBigNum =
		targetAccountBankBalance.withdrawLimitBase || BigNum.zero();

	const bufferedBorrowMax = useMemo(() => {
		return (targetAccountBankBalance.withdrawLimitBase || BigNum.zero()).scale(
			99,
			100
		);
	}, [targetAccountBankBalance.withdrawLimitBase?.toString()]);

	// Check if borrows are disabled
	const shouldBlockBorrows = !selectedTargetAccount?.client;

	const marketReduceOnly = ENUM_UTILS.match(
		spotMarketAccount?.status,
		MarketStatus.REDUCE_ONLY
	);

	const marketProtected = ENUM_UTILS.match(
		spotMarketAccount?.assetTier,
		AssetTier.PROTECTED
	);

	// Get current total borrowed amount
	const totalBorrowedAmount = useMemo(() => {
		return targetAccountBankBalance.liabilityBaseBalance || BigNum.zero();
	}, [targetAccountBankBalance]);

	const {
		isBankrupt: selectedAccountIsBankrupt,
		isBeingLiquidated: selectedAccountIsBeingLiqd,
		isAtRisk: selectedAccountIsAtRisk,
	} = useAccountLiquidationFlags(selectedTargetAccountKey);

	const isPureWithdrawal =
		collatTransferAmountBignum.gtZero() &&
		targetAccountBankBalance.netBaseBalance.gte(collatTransferAmountBignum);

	const isPartialWithdrawal =
		collatTransferAmountBignum.gtZero() &&
		targetAccountBankBalance.netBaseBalance.gtZero() &&
		collatTransferAmountBignum.gt(targetAccountBankBalance.netBaseBalance);

	// Gate note: Borrow historically has no ceiling clause — the input clamps
	// to the withdraw limit on change, but the submit gate never re-checked
	// it. Preserved by omitting `maxAmount` (candidate bug, do not silently
	// unify).
	const { canSubmit: allowConfirmation } = useCollateralActionGate({
		amount: collatTransferAmountBignum,
		amountInput: collatTransferAmount,
		isSubmitting: submitting,
		extraChecks: [
			{
				id: 'MARKET_PROTECTED',
				blocked: marketProtected && !isPureWithdrawal,
			},
			{
				id: 'MARKET_REDUCE_ONLY',
				blocked: marketReduceOnly && !isPureWithdrawal,
			},
			{
				id: 'BORROWS_BLOCKED',
				blocked: shouldBlockBorrows && !isPureWithdrawal,
			},
			{ id: 'ACCOUNT_BANKRUPT', blocked: selectedAccountIsBankrupt },
			{
				id: 'ACCOUNT_BEING_LIQUIDATED',
				blocked: selectedAccountIsBeingLiqd,
			},
		],
	});

	const handleBorrow = useCallback(async () => {
		setSubmitting(true);

		const collateralAmount = parseInputBigNumOrZero(
			collatTransferAmount,
			selectedCollateralMarket.precisionExp
		);

		const createsBorrowLiability =
			targetAccountBankBalance.assetBaseBalance.lt(collateralAmount);

		const result = await withdrawCollateral({
			amount: collateralAmount,
			spotMarketConfig: selectedCollateralMarket,
			userAccount: selectedTargetAccount?.client?.getUserAccount() ?? undefined,
			isBorrow: createsBorrowLiability,
		});

		setSubmitting(false);

		captureEvent('collateral_borrow_submitted', {
			spot_market_symbol: selectedCollateralMarket.symbol,
			borrow_amount: collatTransferAmount,
		});

		if (result === true) {
			hide();
		}
	}, [
		collatTransferAmount,
		selectedCollateralMarket,
		targetAccountBankBalance.assetBaseBalance,
		selectedTargetAccount,
		selectedTargetAccountKey,
		captureEvent,
		hide,
	]);

	const handleValueChange = (newValue?: string) => {
		if (newValue !== undefined) {
			setIsMax(false);

			// Set input to max amount if exceeds limit
			if (newValue === '') {
				setCollatTransferAmount(newValue);
				return;
			}

			const inputBigNum = parseInputBigNumOrZero(
				newValue,
				selectedCollateralMarket.precisionExp
			);
			if (inputBigNum.lte(targetAccountBankBalance.withdrawLimitBase)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(
					targetAccountBankBalance.withdrawLimitBase.printShort()
				);
				setIsMax(true);
			}
		}
	};

	const handleMax = () => {
		setCollatTransferAmount(bufferedBorrowMax.printShort());
		setIsMax(true);
	};

	// Update liquidation price when amount changes
	useEffect(() => {
		if (spotMarketAccount) {
			if (selectedTargetAccount?.client) {
				const afterLiqPrice = BigNum.from(
					selectedTargetAccount.client.spotLiquidationPrice(
						spotMarketAccount.marketIndex,
						collatTransferAmountBignum?.neg()?.val
					),
					PRICE_PRECISION_EXP
				);

				setAfterLiqPriceBigNum(afterLiqPrice);
			}
		}
	}, [
		collatTransferAmountBignum?.toString(),
		spotMarketAccount?.marketIndex,
		selectedTargetAccount?.userKey,
	]);

	// If max is selected, keep input value in sync with max. allowed value
	useEffect(() => {
		if (isMax) {
			setCollatTransferAmount(bufferedBorrowMax.printShort());
		}
	}, [isMax, bufferedBorrowMax.printShort()]);

	// Reset transfer amount if market selection changes
	useEffect(() => {
		setCollatTransferAmount('');
	}, [selectedCollateralMarket?.symbol]);

	const filterOutMarketIndices = useMemo(() => {
		return borrowData
			.filter((data) =>
				ENUM_UTILS.match(data.spotMarketAccount.assetTier, AssetTier.PROTECTED)
			) // Borrow disabled for protected assets
			.map((data) => data.bankIndex);
	}, [borrowData]);

	return (
		<BaseModal.Container onClose={hide} dataTestId="borrow-modal">
			<BaseModal.Title onClose={hide} closeButtonTestId="borrow-modal-close">
				Borrow
			</BaseModal.Title>
			<BaseModal.Body>
				<span className="text-text-default">
					Borrowed amounts are withdrawn to your wallet.
				</span>

				{availableSubaccounts.length > 1 && (
					<SubAccountSelector
						mode="withdraw"
						accountLabel="Account"
						selectedTargetAccountKey={selectedTargetAccountKey}
						selectedPoolId={selectedPoolId}
						handleTargetAccountChange={handleTargetAccountChange}
					/>
				)}

				<div className="flex flex-col gap-2">
					<CollateralInputV2
						dataTestId="borrow-modal-amount-input"
						maxAmount={targetAccountBankBalance.withdrawLimitBase}
						selectedMarket={selectedCollateralMarket}
						onChangeValue={handleValueChange}
						onChangeMarket={(val) => updateCollateralIndex(val.marketIndex)}
						value={
							isMax
								? targetAccountBankBalance.withdrawLimitBase.printShort()
								: collatTransferAmount
						}
						label="Assets and amount"
						source={{
							type: 'default',
							filterOutMarketIndices,
						}}
						poolId={selectedPoolId}
						disabled={marketProtected}
						fullScreenPopover={isMobile}
						fullScreenPopoverTitle="Select Asset"
					/>
					{marketReduceOnly && (
						<InlineAlert
							variant="warning"
							title="Borrows are currently disabled on this market."
						/>
					)}

					{!marketReduceOnly && (
						<>
							<BorrowLimitsDisplay
								borrowLimitBigNum={borrowLimitBigNum}
								onAmountSelect={(val) => {
									setIsMax(false);
									setCollatTransferAmount(val);
								}}
								onMaxSelect={handleMax}
							/>

							<BorrowAPYDisplay
								selectedCollatBank={selectedCollateralMarket}
								borrowAPYBigNum={borrowApyBigNum}
							/>
						</>
					)}
				</div>

				<BorrowUnsettledAmountMessage />

				{selectedAccountIsBankrupt ? (
					<InlineAlert
						variant="error"
						title="Withdrawal is disabled while this account is bankrupt."
					/>
				) : selectedAccountIsBeingLiqd ? (
					<InlineAlert
						variant="error"
						title="Withdrawal is disabled during liquidation. Please try again later."
					/>
				) : (
					selectedAccountIsAtRisk && (
						<InlineAlert
							variant="warning"
							title="Your account is close to liquidation, so there is no collateral free to withdraw or borrow against. Deposit collateral or reduce your positions first."
						/>
					)
				)}

				{isPureWithdrawal && (
					<span>
						You already have{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						deposits. We&apos;ll withdraw{' '}
						<span className="text-text-default">
							{collatTransferAmountBignum.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						to your wallet, leaving a net deposit of{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance
								.sub(collatTransferAmountBignum)
								.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>
						.
					</span>
				)}

				{isPartialWithdrawal && (
					<span>
						You already have{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						deposits. We&apos;ll use that first, so your net borrow will be{' '}
						<span className="text-text-default">
							{collatTransferAmountBignum
								.sub(targetAccountBankBalance.netBaseBalance)
								.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>
						.
					</span>
				)}

				<BalanceChanges
					totalBorrowedAmount={totalBorrowedAmount.add(
						collatTransferAmountBignum
					)}
					afterLiqPriceBigNum={afterLiqPriceBigNum}
					selectedCollatBank={selectedCollateralMarket}
				/>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={handleBorrow}
					disabled={!allowConfirmation}
					isLoading={submitting}
					dataTestId="borrow-modal-submit"
				>
					Confirm {isPureWithdrawal ? 'Withdrawal' : 'Borrow'}
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
};

export default React.memo(BorrowModal);
